import type { ReactNode } from "react";
import type { RealEstateTaxDetails } from "../engine";
import { formatMxn } from "../engine/money";
import { REAL_ESTATE_SOURCES } from "../engine/real-estate-isr";

const decimalNumber = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

const percentageNumber = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatPercent(value: number): string {
  return `${percentageNumber.format(value)}%`;
}

function Formula({ children }: { children: ReactNode }) {
  return (
    <div className="explanation-formula">
      <span>La cuenta</span>
      <strong>{children}</strong>
    </div>
  );
}

function LegalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      className="explanation-legal"
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      Fundamento: {children} ↗
    </a>
  );
}

function ValueList({
  items,
}: {
  items: Array<{ label: string; value: ReactNode }>;
}) {
  return (
    <dl className="explanation-values">
      {items.map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ExplanationStep({
  number,
  title,
  summary,
  resultLabel,
  resultValue,
  children,
  open = false,
}: {
  number: number;
  title: string;
  summary: string;
  resultLabel: string;
  resultValue: string;
  children: ReactNode;
  open?: boolean;
}) {
  return (
    <details className="explanation-step" open={open}>
      <summary>
        <span className="explanation-step__number">{number}</span>
        <span className="explanation-step__heading">
          <strong>{title}</strong>
          <small>{summary}</small>
        </span>
        <span className="explanation-step__result">
          <small>{resultLabel}</small>
          <strong>{resultValue}</strong>
        </span>
        <span className="explanation-step__chevron" aria-hidden="true" />
      </summary>
      <div className="explanation-step__body">{children}</div>
    </details>
  );
}

function Condition({ met, children }: { met: boolean; children: ReactNode }) {
  return (
    <li className={met ? "condition condition--met" : "condition condition--not-met"}>
      <span aria-hidden="true">{met ? "✓" : "×"}</span>
      {children}
    </li>
  );
}

export function RealEstateExplanation({
  details,
}: {
  details: RealEstateTaxDetails;
}) {
  const acquisitionFactor =
    details.deductions.find((line) => line.key === "land")?.updateFactor ?? 1;
  const tariff = details.tariffCalculation;
  const tariffUpper = tariff.upperLimit
    ? formatMxn(tariff.upperLimit)
    : "en adelante";

  return (
    <section className="real-estate-explanation" aria-labelledby="isr-paso-a-paso">
      <header className="real-estate-explanation__header">
        <div>
          <span className="eyebrow">Explicación paso a paso</span>
          <h3 id="isr-paso-a-paso">¿Cómo llegamos a este ISR?</h3>
          <p>
            Abre cada paso para ver qué significa, la operación con tus cifras y
            el fundamento que se aplicó.
          </p>
        </div>
        <span className="explanation-count">8 pasos</span>
      </header>

      <div className="explanation-steps">
        <ExplanationStep
          number={1}
          title="Tomamos la parte de la venta que te corresponde"
          summary="El impuesto se calcula sobre tu participación, no necesariamente sobre todo el inmueble."
          resultLabel="Tu contraprestación"
          resultValue={formatMxn(details.sellerSalePrice)}
          open
        >
          <p className="explanation-plain">
            Primero partimos del precio total y lo multiplicamos por tu porcentaje
            de propiedad. Éste es el ingreso de la operación que corresponde a la
            persona enajenante.
          </p>
          <Formula>
            {formatMxn(details.totalSalePrice)} × {formatPercent(details.ownershipPercentage)} = {" "}
            {formatMxn(details.sellerSalePrice)}
          </Formula>
          <ValueList
            items={[
              { label: "Precio total del inmueble", value: formatMxn(details.totalSalePrice) },
              { label: "Participación capturada", value: formatPercent(details.ownershipPercentage) },
            ]}
          />
        </ExplanationStep>

        <ExplanationStep
          number={2}
          title="Calculamos la exención de casa habitación"
          summary="Comprobamos los requisitos y el límite de 700,000 UDI por persona."
          resultLabel="Ingreso exento"
          resultValue={formatMxn(details.exemptConsideration)}
        >
          <p className="explanation-plain">
            La exención sólo puede aplicarse a la parte usada como casa habitación
            y nunca puede superar el equivalente a 700,000 UDI.
          </p>
          <ul className="exemption-conditions">
            <Condition met={details.exemptionConditions.isHouse}>
              El inmueble se capturó como casa habitación.
            </Condition>
            <Condition met={details.exemptionConditions.requested}>
              Se solicitó aplicar la exención.
            </Condition>
            <Condition met={details.exemptionConditions.formalizedBeforeNotary}>
              La operación se formaliza ante fedatario.
            </Condition>
            <Condition met={details.exemptionConditions.notClaimedLastThreeYears}>
              No se utilizó esta exención en los tres años anteriores.
            </Condition>
          </ul>
          <Formula>
            700,000 UDI × {decimalNumber.format(details.udiValue)} = {" "}
            {formatMxn(details.exemptionCap)} de límite
          </Formula>
          <ValueList
            items={[
              {
                label: "Parte de la venta destinada a casa habitación",
                value: `${formatMxn(details.houseConsideration)} (${formatPercent(details.houseUsePercentage)})`,
              },
              { label: "Límite de la exención", value: formatMxn(details.exemptionCap) },
              {
                label: "Monto que se toma como exento",
                value: details.exemptionEligible
                  ? `El menor de los dos: ${formatMxn(details.exemptConsideration)}`
                  : "No aplica porque falta al menos un requisito",
              },
            ]}
          />
          <LegalLink href={REAL_ESTATE_SOURCES.exemption}>
            LISR, artículo 93, fracción XIX, inciso a)
          </LegalLink>
        </ExplanationStep>

        <ExplanationStep
          number={3}
          title="Separamos la parte que sí paga impuesto"
          summary="Al ingreso que te corresponde le restamos la exención aplicable."
          resultLabel="Ingreso no exento"
          resultValue={formatMxn(details.taxableConsideration)}
        >
          <p className="explanation-plain">
            Esta cifra todavía no es la ganancia. Es únicamente la parte del precio
            de venta que seguirá al cálculo después de quitar la exención.
          </p>
          <Formula>
            {formatMxn(details.sellerSalePrice)} − {formatMxn(details.exemptConsideration)} = {" "}
            {formatMxn(details.taxableConsideration)}
          </Formula>
          <ValueList
            items={[
              {
                label: "Proporción no exenta",
                value: formatPercent(details.nonExemptRatio * 100),
              },
              {
                label: "¿Para qué sirve esta proporción?",
                value: "Para tomar la misma parte de los costos y gastos deducibles.",
              },
            ]}
          />
          <LegalLink href={REAL_ESTATE_SOURCES.exemption}>
            LISR, artículo 93, fracción XIX, inciso a)
          </LegalLink>
        </ExplanationStep>

        <ExplanationStep
          number={4}
          title="Actualizamos y prorrateamos los costos deducibles"
          summary="Cada costo se actualiza y sólo se deduce en la proporción no exenta."
          resultLabel="Deducciones totales"
          resultValue={formatMxn(details.deductibleTotal)}
        >
          <p className="explanation-plain">
            El terreno conserva su base; la construcción y las mejoras pierden 3%
            por cada año transcurrido, sin bajar de 20% de su costo inicial. Después
            se actualizan con INPC y se aplica la proporción no exenta.
          </p>
          <Formula>
            INPC {decimalNumber.format(details.salePreviousMonthInpc)} ÷ INPC {" "}
            {decimalNumber.format(details.acquisitionInpc)} = factor {" "}
            {decimalNumber.format(acquisitionFactor)}
          </Formula>

          <div className="deduction-explanation-list">
            {details.deductions.map((line) =>
              line.key === "minimum-acquisition-cost" ? (
                <article className="deduction-explanation deduction-explanation--minimum" key={line.key}>
                  <div className="deduction-explanation__heading">
                    <div>
                      <span className="deduction-badge">Piso legal</span>
                      <h4>{line.label}</h4>
                    </div>
                    <strong>{formatMxn(line.deductible)}</strong>
                  </div>
                  <p>
                    Como el costo actualizado de adquisición quedó por debajo de
                    10% de la venta, agregamos únicamente la diferencia necesaria.
                  </p>
                  <Formula>
                    {formatMxn(details.minimumAcquisitionCost)} − {" "}
                    {formatMxn(details.updatedAcquisitionCostBeforeMinimum)} = {" "}
                    {formatMxn(details.minimumAcquisitionCostAdjustment)}
                  </Formula>
                  <Formula>
                    {formatMxn(details.minimumAcquisitionCostAdjustment)} × {" "}
                    {formatPercent(details.nonExemptRatio * 100)} = {" "}
                    {formatMxn(line.deductible)} deducible
                  </Formula>
                </article>
              ) : (
                <article className="deduction-explanation" key={line.key}>
                  <div className="deduction-explanation__heading">
                    <h4>{line.label}</h4>
                    <strong>{formatMxn(line.deductible)}</strong>
                  </div>
                  <ValueList
                    items={[
                      { label: "Importe comprobado", value: formatMxn(line.original) },
                      {
                        label: "Base después de reducción por antigüedad",
                        value: formatMxn(line.adjustedBase),
                      },
                      { label: "Factor de actualización", value: decimalNumber.format(line.updateFactor) },
                      { label: "Importe actualizado", value: formatMxn(line.updated) },
                    ]}
                  />
                  <Formula>
                    {formatMxn(line.updated)} × {formatPercent(details.nonExemptRatio * 100)} = {" "}
                    {formatMxn(line.deductible)} deducible
                  </Formula>
                </article>
              ),
            )}
          </div>

          <div className="minimum-cost-check">
            <span>{details.minimumAcquisitionCostAdjustment > 0 ? "Se aplicó el piso legal" : "El costo ya supera el piso legal"}</span>
            <p>
              Costo actualizado antes del piso: {formatMxn(details.updatedAcquisitionCostBeforeMinimum)} ·
              mínimo de 10%: {formatMxn(details.minimumAcquisitionCost)}.
            </p>
          </div>
          <LegalLink href={REAL_ESTATE_SOURCES.deductions}>
            LISR, artículo 121; actualización conforme al artículo 124
          </LegalLink>
        </ExplanationStep>

        <ExplanationStep
          number={5}
          title="Obtenemos la ganancia gravable"
          summary="Al ingreso no exento le restamos todos los costos deducibles."
          resultLabel="Ganancia gravable"
          resultValue={formatMxn(details.taxableGain)}
        >
          <p className="explanation-plain">
            Ésta es la utilidad fiscal estimada de la operación. Si las deducciones
            fueran mayores, esta calculadora deja la ganancia en cero y no modela la pérdida.
          </p>
          <Formula>
            {formatMxn(details.taxableConsideration)} − {formatMxn(details.deductibleTotal)} = {" "}
            {formatMxn(details.taxableGain)}
          </Formula>
          <LegalLink href={REAL_ESTATE_SOURCES.deductions}>
            LISR, artículo 121
          </LegalLink>
        </ExplanationStep>

        <ExplanationStep
          number={6}
          title="Repartimos la ganancia entre los años transcurridos"
          summary="La ley permite dividirla entre un máximo de 20 años para aplicar la tarifa."
          resultLabel={`Ganancia por año (${details.elapsedYears})`}
          resultValue={formatMxn(details.gainPerYear)}
        >
          <p className="explanation-plain">
            No se calcula la tarifa sobre toda la ganancia de golpe. Primero se
            obtiene una ganancia promedio por año; después el impuesto anual se
            multiplica por el mismo número de años.
          </p>
          <Formula>
            {formatMxn(details.taxableGain)} ÷ {details.elapsedYears} años = {" "}
            {formatMxn(details.gainPerYear)} por año
          </Formula>
          <ValueList
            items={[
              { label: "Años completos realmente transcurridos", value: details.actualElapsedYears },
              { label: "Años usados fiscalmente", value: `${details.elapsedYears} (máximo 20)` },
            ]}
          />
          <LegalLink href={REAL_ESTATE_SOURCES.federalPayment}>
            LISR, artículo 126
          </LegalLink>
        </ExplanationStep>

        <ExplanationStep
          number={7}
          title="Aplicamos la tarifa y calculamos el ISR provisional"
          summary="Ubicamos la ganancia anual en su renglón, sumamos la cuota fija y multiplicamos por los años."
          resultLabel="ISR antes del crédito estatal"
          resultValue={formatMxn(details.federalTaxBeforeStateCredit)}
        >
          <p className="explanation-plain">
            {details.gainPerYear === 0
              ? "Como no hay ganancia gravable por año, la tarifa produce un impuesto de cero."
              : `La ganancia por año cae en el renglón que va de ${formatMxn(tariff.lowerLimit)} a ${tariffUpper}. Sólo el excedente sobre el límite inferior se multiplica por la tasa.`}
          </p>
          <div className="tariff-walkthrough">
            <ValueList
              items={[
                { label: "Ganancia por año", value: formatMxn(details.gainPerYear) },
                { label: "Límite inferior", value: formatMxn(tariff.lowerLimit) },
                { label: "Excedente", value: formatMxn(tariff.excess) },
                { label: "Tasa marginal", value: formatPercent(tariff.rate * 100) },
                { label: "Impuesto sobre el excedente", value: formatMxn(tariff.marginalTax) },
                { label: "Cuota fija", value: formatMxn(tariff.fixedFee) },
              ]}
            />
            <Formula>
              ({formatMxn(tariff.excess)} × {formatPercent(tariff.rate * 100)}) + {" "}
              {formatMxn(tariff.fixedFee)} = {formatMxn(tariff.taxPerYear)} por año
            </Formula>
            <Formula>
              {formatMxn(tariff.taxPerYear)} × {details.elapsedYears} años = {" "}
              {formatMxn(details.federalTaxBeforeStateCredit)}
            </Formula>
          </div>
          <LegalLink href={REAL_ESTATE_SOURCES.tariff}>
            Regla 3.15.4 y LISR, artículo 126
          </LegalLink>
        </ExplanationStep>

        <ExplanationStep
          number={8}
          title="Distribuimos el pago entre el estado y la Federación"
          summary="El pago estatal forma parte del ISR total: se acredita y no se suma dos veces."
          resultLabel="Pago provisional total"
          resultValue={formatMxn(details.federalTaxBeforeStateCredit)}
        >
          <p className="explanation-plain">
            Primero se calcula 5% de la ganancia para la entidad federativa. Si ese
            importe excediera el ISR provisional, se limita al propio ISR. Lo pagado
            al estado se resta del entero federal.
          </p>
          <Formula>
            {formatMxn(details.taxableGain)} × 5% = {" "}
            {formatMxn(details.statePaymentAtFivePercent)}
          </Formula>
          <ValueList
            items={[
              { label: "Pago a la entidad federativa", value: formatMxn(details.statePayment) },
              { label: "Saldo a la Federación", value: formatMxn(details.federalBalance) },
            ]}
          />
          <div className="tax-reconciliation">
            <span>Comprobación final</span>
            <strong>
              {formatMxn(details.statePayment)} + {formatMxn(details.federalBalance)} = {" "}
              {formatMxn(details.federalTaxBeforeStateCredit)}
            </strong>
            <small>El pago estatal ya está incluido en el total.</small>
          </div>
          <LegalLink href={REAL_ESTATE_SOURCES.statePayment}>
            LISR, artículo 127
          </LegalLink>
        </ExplanationStep>
      </div>
    </section>
  );
}
